import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import PageTitle from 'Components/PageTitle'
import { MultiCodeCampaignList } from 'Services/SalesManagement/Features/Campaign/Components/MultiCodeCampaignList'
import { SingleCodeCampaignList } from 'Services/SalesManagement/Features/Campaign/Components/SingleCodeCampaignList'
import {
  type CampaignParamShape,
  CampaignType,
  type CampaignTypes,
  fetchMultiCodeCampaignList,
  fetchSingleCodeCampaignList
} from 'Services/SalesManagement/Features/Campaign/campaignSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import useQuery from 'Util/getQueryParamsHook'
import useHash from 'Util/hooks/useHash'
import AddIcon from '@mui/icons-material/Add'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Tab } from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { type default as React, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

export default function CampaignListPage() {
  const title = t`Campaigns`
  const dispatch = useAppDispatch()

  const queryParams = useQuery()
  const navigate = useNavigate()
  const hash = useHash()

  const { singleCodeCampaigns, multiCodeCampaigns, count, loading } = useTypedSelector(
    state => state.campaign
  )

  const defaultTab = (hash as CampaignTypes) || CampaignType.singleCode
  const [tabValue, setTabValue] = useState<CampaignTypes>(defaultTab)

  const [params, setParams] = useState<CampaignParamShape>({
    ordering: queryParams.get('ordering') ?? '-created',
    page: queryParams.get('page') ? parseInt(queryParams.get('page') as string) : 1
  })

  useEffect(() => {
    switch (tabValue) {
      case CampaignType.singleCode:
        dispatch(fetchSingleCodeCampaignList(params)).then(() => {
          setQueryParams()
        })
        break
      case CampaignType.multiCode:
        dispatch(fetchMultiCodeCampaignList(params)).then(() => {
          setQueryParams()
        })
        break
      default:
        setTabValue(defaultTab)
        dispatch(fetchSingleCodeCampaignList(params)).then(() => {
          setQueryParams()
        })
    }
  }, [dispatch, params, tabValue])

  const setQueryParams = () => {
    const query: string[] = []
    Object.keys(params).forEach((key: string) => {
      const value = params[key as keyof CampaignParamShape]
      if (value) {
        query.push(key + '=' + value)
      }
    })
    const queryString = query.join('&')

    navigate({
      pathname: location.pathname,
      search: queryString,
      hash: tabValue
    })
  }

  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = params.ordering.includes('-')
    setParams(prevState => ({ ...prevState, ordering: `${isAsc ? '' : '-'}${property}` }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({ ...prevState, page: newPage + 1 }))
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: CampaignType) => {
    navigate(`#${newValue}`)
    setTabValue(newValue)
  }

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SALES_MANAGEMENT_ROOT_URL
    },
    {
      title: title
    }
  ]

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <BackButton link={SALES_MANAGEMENT_ROOT_URL} />
        <PageTitle>{title}</PageTitle>
        <Typography component="p" variant="subtitle1" sx={{ fontSize: '15px', mb: 4 }}>
          {t`In the campaign section you can control single code campaigns and multi code campaigns.`}
        </Typography>
      </Box>
      <TabContext value={tabValue}>
        <Paper>
          <TabList onChange={handleTabChange} aria-label={t`Campaign tab list`}>
            <Tab label={t`Single code`} value={CampaignType.singleCode} />
            <Tab label={t`Multi code`} value={CampaignType.multiCode} />
          </TabList>
        </Paper>
        <TabPanel sx={{ px: 0, pt: 1 }} value={CampaignType.singleCode}>
          <CustomButton
            sx={{ mt: 1, mb: 2 }}
            onClick={() =>
              navigate(`${SALES_MANAGEMENT_ROOT_URL}/campaigns/${CampaignType.singleCode}/create/`)
            }
            size="small"
            startIcon={<AddIcon />}
          >
            {t`Create new single code campaign`}
          </CustomButton>
          <SingleCodeCampaignList
            rows={singleCodeCampaigns}
            loading={loading}
            params={params}
            handleRequestSort={handleRequestSort}
            handleChangePage={handleChangePage}
            count={count}
          />
        </TabPanel>
        <TabPanel sx={{ px: 0, pt: 1 }} value={CampaignType.multiCode}>
          <CustomButton
            sx={{ mt: 1, mb: 2 }}
            onClick={() =>
              navigate(`${SALES_MANAGEMENT_ROOT_URL}/campaigns/${CampaignType.multiCode}/create/`)
            }
            size="small"
            startIcon={<AddIcon />}
          >
            {t`Create new multi code campaign`}
          </CustomButton>
          <MultiCodeCampaignList
            rows={multiCodeCampaigns}
            loading={loading}
            params={params}
            handleRequestSort={handleRequestSort}
            handleChangePage={handleChangePage}
            count={count}
          />
        </TabPanel>
      </TabContext>
    </Box>
  )
}
